import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isMicrofinanceWorkspace, isRouteAllowed, firstAllowedRoute } from "@/lib/routeAccess";
import { toast } from "sonner";

/**
 * Wraps a route element. If the current user's role is not permitted to
 * view this route, redirects to the first allowed page and shows a toast.
 * Defence-in-depth on top of `ProtectedRoute` (auth) and backend
 * `require_roles` guards.
 */
export default function RoleGuard({ children }) {
  const { user, tenant } = useAuth();
  const location = useLocation();

  const role = user?.role;
  const roleGrants = tenant?.active_role_module_grants || [];
  let allowed = isRouteAllowed(location.pathname, role, roleGrants, tenant);
  const mfiManagerRoute = ["tenant_admin", "mfi_branch_manager"].includes(role) &&
    isMicrofinanceWorkspace(tenant) && (location.pathname === "/field" || location.pathname.startsWith("/field/") ||
      location.pathname === "/microfinance" || location.pathname.startsWith("/microfinance/"));
  // Enforce per-tenant module whitelist (Super Admin toggles). Super admins
  // are always allowed so they can still administer everything.
  if (allowed && role !== "super_admin" && user?.assigned_role !== "demo" && !mfiManagerRoute) {
    const enabled = tenant?.enabled_modules;
    if (Array.isArray(enabled)) {
      const routePath = location.pathname === "/field" ? "/microfinance" : location.pathname;
      const rootKey = Object.keys(enabled.reduce((a, k) => (a[k] = 1, a), {}))
        .sort((a, b) => b.length - a.length)
        .find((k) => routePath === k || routePath.startsWith(k + "/"));
      if (!rootKey) allowed = false;
    }
  }

  useEffect(() => {
    if (!allowed && role) {
      toast.error(`Access denied — your role (${role}) can't open ${location.pathname}.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, location.pathname]);

  if (!role) return children;
  if (!allowed) return <Navigate to={firstAllowedRoute(role, roleGrants, tenant)} replace />;
  return children;
}
